import { useState } from "react";
import { Maximize2 } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { Lightbox } from "./Lightbox";

const LOOKS = [
  { id: "habesha", title: "With your habesha dress", kaba: "Crimson Cape", tip: "The classic, crowned. Drape it over a white kemis with a tibeb border for holidays, church and weddings.", src: "/images/real/wear_habesha.jpg" },
  { id: "jeans", title: "With jeans", kaba: "Midnight Majesty", tip: "Velvet meets denim. A white tee, straight jeans and sneakers — royalty on a Saturday.", src: "/images/real/wear_jeans.jpg" },
  { id: "skirt", title: "With a skirt", kaba: "Midnight Majesty", tip: "A pleated midi skirt and a fitted top let the waist-length cut frame your silhouette.", src: "/images/real/wear_skirt.jpg" },
  { id: "suit", title: "With a fancy suit", kaba: "Crimson Cape", tip: "Draped over a tailored pantsuit, it turns a boardroom look into a statement.", src: "/images/real/wear_suit.jpg" },
  { id: "blackmamba", title: "For a night out", kaba: "Black Mamba", tip: "Over an ivory satin jumpsuit with gold heels, the crystal fringe catches every light in the room.", src: "/images/real/wear_blackmamba.jpg" },
  { id: "wedding", title: "As a wedding guest", kaba: "Black Mamba", tip: "Over an emerald satin midi skirt with gold sandals — elegant, celebratory and never underdressed.", src: "/images/real/wear_wedding.jpg" },
];

const PHOTOS = LOOKS.map((l) => ({ src: l.src, alt: `${l.kaba} styled ${l.title.toLowerCase()}`, label: `${l.title} · ${l.kaba}` }));

const Look = ({ look, i, onOpen }) => (
  <Reveal delay={(i % 3) * 0.08} className={i % 3 === 1 ? "lg:pt-16" : ""}>
    <article data-testid={`wear-look-${look.id}`}>
      <button onClick={onOpen} aria-label={`Zoom ${look.title}`} data-testid={`wear-photo-${look.id}`}
        className="group relative block aspect-[3/4] w-full cursor-zoom-in overflow-hidden border border-gold/20 bg-ink2">
        <img src={look.src} alt={PHOTOS[i].alt} loading="lazy"
          className="h-full w-full object-cover object-top transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-ink/80 to-transparent" />
        <div className="absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-ink/70 to-transparent" />
        <span className="absolute left-4 top-3 font-display text-5xl italic text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">{String(i + 1).padStart(2, "0")}</span>
        <span className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-gold/40 bg-ink/60 text-gold backdrop-blur-md lg:opacity-0 lg:transition-opacity lg:duration-300 lg:group-hover:opacity-100">
          <Maximize2 className="h-4 w-4" />
        </span>
        <span className="absolute bottom-3 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-cream">{look.kaba}</span>
      </button>
      <h3 className="mt-5 font-display text-3xl text-cream">{look.title}</h3>
      <p className="mt-2 max-w-sm text-sm font-light leading-relaxed text-stone2 sm:text-base">{look.tip}</p>
    </article>
  </Reveal>
);

export const HowToWear = () => {
  const [index, setIndex] = useState(null);
  return (
    <section id="wear" data-testid="how-to-wear-section" className="mx-auto max-w-[1400px] px-5 pb-40 pt-12 sm:px-10 lg:pb-52">
      <div className="mb-14 grid gap-6 border-b border-gold/15 pb-8 md:grid-cols-2 md:items-end">
        <Reveal>
          <Eyebrow>How to wear your kaba</Eyebrow>
          <h2 className="mt-5 font-display text-4xl font-light tracking-tight text-cream sm:text-5xl lg:text-6xl">
            One kaba, <span className="italic text-white">every</span> outfit.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="max-w-md text-sm font-light leading-relaxed text-stone2 sm:text-base md:justify-self-end">
            Waist length and one size fits all (XS to 2XL), it sits beautifully over almost anything — from your habesha dress to your favourite jeans.
          </p>
        </Reveal>
      </div>
      <div className="grid gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
        {LOOKS.map((l, i) => <Look key={l.id} look={l} i={i} onOpen={() => setIndex(i)} />)}
      </div>
      <Lightbox images={PHOTOS} index={index} setIndex={setIndex} onClose={() => setIndex(null)} />
    </section>
  );
};
